/**
 * presetRange.js — почему «Кожа» и «Мышцы» показывали чёрное окно.
 *
 * Объёмный пресет это две кривые: цвет и прозрачность, заданные в точках по
 * шкале плотности. Все КТ-пресеты написаны в единицах Хаунсфилда: у «Кожи»
 * прозрачность отлична от нуля только между −200 и +200 HU, у «Мышц» — между
 * −80 и +500.
 *
 * У КТ это ровно те числа, что лежат в данных, и всё работает. А у МР шкалы
 * HU нет вовсе: значения там условные, обычно 0…1500, и вся ткань оказывается
 * ЗА пределами окна пресета. Пресет применяется без единой ошибки — и окно
 * чернеет. Со стороны это выглядит как «кнопка не работает», хотя нажатие
 * дошло, и в журнале стоит «✅ Preset applied».
 *
 * Здесь два вопроса, оба чистой арифметикой:
 *   1. В каком диапазоне пресет вообще что-то рисует (support).
 *   2. Как перенести его точки на шкалу конкретной серии, если попадания нет.
 *
 * Перенос делается только для НЕ-КТ. У КТ числа означают ткань: жир это −100,
 * мышца +40, кость +700 — растягивать их под гистограмму значит врать врачу о
 * плотности. У МР число само по себе не значит ничего, и перенос как раз
 * возвращает пресету смысл: «нижняя треть яркости», «средняя», «верхняя».
 */

/**
 * Диапазон, где прозрачность больше нуля.
 *
 * Считаем по точкам кривой: берём от последней нулевой точки перед первой
 * ненулевой до первой нулевой после последней ненулевой — то есть с учётом
 * того, что между точками значения интерполируются.
 *
 * @param {{opacity: Array<[number, number]>}} preset
 * @returns {{lo: number, hi: number} | null}
 */
export function presetSupport(preset) {
  const pts = preset?.opacity;
  if (!Array.isArray(pts) || pts.length < 2) return null;

  const clean = pts
    .filter(p => Array.isArray(p) && p.length >= 2 && Number.isFinite(p[0]) && Number.isFinite(p[1]))
    .slice()
    .sort((a, b) => a[0] - b[0]);
  if (clean.length < 2) return null;

  let first = -1, last = -1;
  for (let i = 0; i < clean.length; i++) {
    if (clean[i][1] > 0) { if (first < 0) first = i; last = i; }
  }
  if (first < 0) return null;   // пресет вообще ничего не показывает

  const lo = clean[Math.max(0, first - 1)][0];
  const hi = clean[Math.min(clean.length - 1, last + 1)][0];
  return (hi > lo) ? { lo, hi } : null;
}

/**
 * Пересекается ли рабочий диапазон пресета с данными настолько, чтобы что-то
 * было видно.
 *
 * @param {{lo:number, hi:number}} support
 * @param {{lo:number, hi:number}} data диапазон значений серии
 * @param {number} [minShare=0.15] какую долю окна пресета обязано покрыть
 * @returns {boolean}
 */
export function supportOverlaps(support, data, minShare = 0.15) {
  if (!support || !data) return true;   // нечем судить — не мешаем
  const lo = Math.max(support.lo, data.lo);
  const hi = Math.min(support.hi, data.hi);
  const overlap = hi - lo;
  if (!(overlap > 0)) return false;
  const width = support.hi - support.lo;
  return width > 0 ? (overlap / width) >= minShare : true;
}

/**
 * Перенести точки пресета с одной шкалы на другую линейно.
 *
 * Прозрачность, цвет и градиент двигаются согласованно: сдвинуть только цвет
 * значило бы раскрасить не ту ткань.
 *
 * @param {object} preset исходный пресет
 * @param {{lo:number, hi:number}} from откуда (обычно его же support)
 * @param {{lo:number, hi:number}} to куда (диапазон значений серии)
 * @returns {object|null} копия пресета с перенесёнными точками
 */
export function rescalePreset(preset, from, to) {
  if (!preset || !from || !to) return null;
  const span = from.hi - from.lo;
  const target = to.hi - to.lo;
  if (!(span > 0) || !(target > 0)) return null;

  const k = target / span;
  const map = (x) => to.lo + (x - from.lo) * k;

  const movePoints = (arr, valueIndexFrom) => {
    if (!Array.isArray(arr)) return arr;
    return arr.map(p => {
      if (!Array.isArray(p) || !p.length) return p;
      const copy = p.slice();
      copy[0] = map(copy[0]);
      return copy;
    });
  };

  const out = { ...preset };
  out.opacity = movePoints(preset.opacity);
  out.color = movePoints(preset.color);
  // Градиент задан в единицах ИЗМЕНЕНИЯ плотности на шаг, а не в самой
  // плотности: его тянем тем же коэффициентом, но от нуля.
  if (Array.isArray(preset.gradient)) {
    out.gradient = preset.gradient.map(p => (Array.isArray(p) ? [p[0] * k, ...p.slice(1)] : p));
  }
  // Расстояние, на котором накапливается непрозрачность, тоже в единицах шкалы.
  if (Number.isFinite(preset.opacityUnitDistance)) {
    out.opacityUnitDistance = preset.opacityUnitDistance;
  }
  out.name = preset.name;
  return out;
}

/** Модальности, у которых шкала — настоящие единицы Хаунсфилда. */
const HU_MODALITIES = new Set(['CT', 'CBCT', 'DX', 'CR', 'XA', 'RF']);

/** Признак «пресет написан в HU» — берём из самого пресета, а не угадываем. */
function _presetIsHU(preset) {
  const m = String(preset?.modality || '').toLowerCase();
  return m === 'ct' || m === 'cbct';
}

/**
 * Решение: применять пресет как есть, перенести на шкалу серии, или сказать,
 * что показывать нечего.
 *
 * Главный признак — не арифметика пересечений, а объявленная модальность
 * пресета. Числовое пересечение обманчиво: у «Кожи» окно −200…+200, у МР-серии
 * данные 0…1400, пересечение 0…200 занимает половину окна пресета — формально
 * «попадает». А по сути в этих 0…200 у МР лежит воздух и шум, вся ткань выше;
 * окно всё равно почернеет. Пресет сам знает, для какой шкалы он написан —
 * это и спрашиваем.
 *
 * Числовая проверка остаётся второй линией: она ловит случай, когда и
 * модальности сошлись, а данных в окне пресета всё равно нет (обрезанный
 * объём, локалайзер).
 *
 * @param {object} p
 * @param {object} p.preset
 * @param {string} p.modality модальность серии
 * @param {{lo:number, hi:number}|null} p.dataRange диапазон значений серии
 * @returns {{action: 'as-is'|'rescale'|'empty', support: object|null, target: object|null, reason: string}}
 */
export function planPresetRange({ preset, modality, dataRange }) {
  const support = presetSupport(preset);
  const mod = String(modality || '').toUpperCase();
  const seriesIsHU = HU_MODALITIES.has(mod);

  if (!support) {
    return { action: 'as-is', support: null, target: null, reason: 'у пресета нет рабочего диапазона' };
  }
  if (!dataRange || !(dataRange.hi > dataRange.lo)) {
    return { action: 'as-is', support, target: null, reason: 'диапазон серии неизвестен' };
  }

  // 1. Пресет написан в HU, а серия не в HU — переносим.
  if (_presetIsHU(preset) && !seriesIsHU) {
    return {
      action: 'rescale',
      support,
      target: dataRange,
      reason: `${mod || 'серия'} без шкалы HU — пресет перенесён на ${Math.round(dataRange.lo)}…${Math.round(dataRange.hi)}`,
    };
  }

  // 2. Модальности сошлись — смотрим, есть ли вообще попадание.
  //
  // [12.0.27] Для шкалы HU — по НАСТОЯЩИМ краям данных (min/max), а не по
  // процентилям 1–99. Кость и контраст занимают меньше процента объёма, и
  // 99-й процентиль артериальной КТ владельца был 467 HU: органный пресет
  // с рабочим окном 75…3000 «попадал» лишь на 13% окна и объявлялся «вне
  // шкалы» — врач на каждое нажатие видел ложную плашку «рассчитан на
  // другую шкалу», хотя пресет применялся верно. Числа КТ означают ткань,
  // и вопрос тут один: есть ли в серии хоть что-то в окне пресета.
  const края = (seriesIsHU && Number.isFinite(dataRange.min) && Number.isFinite(dataRange.max) && dataRange.max > dataRange.min)
    ? { lo: dataRange.min, hi: dataRange.max } : dataRange;
  if (supportOverlaps(support, края)) {
    return { action: 'as-is', support, target: null, reason: 'пресет попадает в данные' };
  }

  if (seriesIsHU) {
    // Двигать числа КТ нельзя: они означают ткань.
    return {
      action: 'empty', support, target: null,
      reason: `диапазон серии ${Math.round(края.lo)}…${Math.round(края.hi)} вне окна пресета ${Math.round(support.lo)}…${Math.round(support.hi)}`,
    };
  }

  return {
    action: 'rescale', support, target: dataRange,
    reason: `окно пресета ${Math.round(support.lo)}…${Math.round(support.hi)} вне данных — перенесено на ${Math.round(dataRange.lo)}…${Math.round(dataRange.hi)}`,
  };
}

export default { presetSupport, supportOverlaps, rescalePreset, planPresetRange };
